import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bot, Send, Loader2, Trash2, Copy, CheckCircle2, Phone, MessageSquare, ExternalLink, AlertCircle, QrCode, Save, KeyRound, Eye, EyeOff, Activity, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QUICK_PROMPTS = [
  { label: 'הזמנות היום', text: 'תראה לי את כל ההזמנות של היום' },
  { label: 'מלאי יניב', text: 'כמה מלאי יש ליניב?' },
  { label: 'מלאי לירן', text: 'כמה מלאי יש ללירן?' },
  { label: 'סיכום היום', text: 'תן לי סיכום יומי של הזמנות והכנסות' },
  { label: 'פייסבוק', text: 'מה הסטטיסטיקות של פייסבוק היום?' },
  { label: 'עזרה', text: 'אילו פקודות אתה מכיר?' },
];

export default function AdminAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [config, setConfig] = useState(null);
  const [copied, setCopied] = useState(false);

  // WaSender config (inline, same fields as AdminWaSender)
  const [wa, setWa] = useState({ api_url: 'https://www.wasenderapi.com', api_key: '', device_id: '' });
  const [savingWa, setSavingWa] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [pat, setPat] = useState('');
  const [showPat, setShowPat] = useState(false);
  const [configuringWh, setConfiguringWh] = useState(false);
  const [autoConfigResult, setAutoConfigResult] = useState(null);

  const chatScrollRef = useRef(null);

  const load = async () => {
    try {
      const [cfg, h, waCfg] = await Promise.all([
        axios.get(`${API}/assistant/config`),
        axios.get(`${API}/assistant/history?session=owner`),
        axios.get(`${API}/admin/wasender/config`),
      ]);
      setConfig(cfg.data);
      setMessages(h.data?.messages || []);
      setWa({
        api_url: waCfg.data?.api_url || 'https://www.wasenderapi.com',
        api_key: waCfg.data?.api_key || '',
        device_id: waCfg.data?.device_id || '',
      });
    } catch {
      toast.error('שגיאה בטעינת עוזר אישי');
    }
  };

  useEffect(() => { load(); }, []);

  // ONLY scroll INSIDE the chat container - never the whole page
  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    const userMsg = { role: 'user', content: text, at: new Date().toISOString() };
    setMessages(m => [...m, userMsg]);
    setInput('');
    try {
      const res = await axios.post(`${API}/assistant/message`, {
        phone: config?.owner_phone || 'owner',
        message: text,
      });
      setMessages(m => [...m, { role: 'assistant', content: res.data.response || '(אין תגובה)', at: new Date().toISOString() }]);
    } catch (e) {
      toast.error('שגיאה בשליחה לעוזר');
      setMessages(m => [...m, { role: 'assistant', content: `שגיאה: ${e?.response?.data?.detail || e.message}`, at: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm('למחוק היסטוריית שיחה?')) return;
    try {
      await axios.delete(`${API}/assistant/history?session=owner`);
      setMessages([]);
      toast.success('היסטוריה נמחקה');
    } catch { toast.error('שגיאה במחיקה'); }
  };

  const saveWaConfig = async () => {
    if (!wa.api_key.trim()) { toast.error('הדבק API Key'); return; }
    setSavingWa(true);
    try {
      await axios.post(`${API}/admin/wasender/config`, wa);
      toast.success('הגדרות WhatsApp נשמרו');
      await load();
    } catch { toast.error('שגיאה בשמירה'); }
    setSavingWa(false);
  };

  const testConnection = async () => {
    if (!config?.owner_phone) { toast.error('חסר מספר בעלים'); return; }
    setTestingConnection(true);
    try {
      const res = await axios.post(`${API}/admin/wasender/send`, {
        phone: `+972${config.owner_phone.replace(/^0/, '')}`,
        message: '🤖 בדיקת חיבור - הבוט מחובר!',
      });
      if (res.data?.success === false) {
        toast.error('נכשל: ' + (res.data.message || 'בדוק API Key'));
      } else {
        toast.success('הודעת בדיקה נשלחה! בדוק את הוואטסאפ שלך 📱');
      }
    } catch (e) {
      toast.error('נכשל: ' + (e?.response?.data?.message || e?.response?.data?.detail || 'בדוק API Key'));
    }
    setTestingConnection(false);
  };

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/assistant/webhook`;

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const r = await axios.get(`${API}/assistant/webhook-log`);
      setWebhookLogs(r.data?.logs || []);
    } catch { /* ignore */ }
    setLoadingLogs(false);
  };

  const autoConfigureWebhook = async () => {
    if (!pat.trim()) return;
    setConfiguringWh(true);
    setAutoConfigResult(null);
    try {
      const r = await axios.post(`${API}/admin/wasender/configure-webhook`, {
        personal_access_token: pat.trim(),
        webhook_url: webhookUrl,
      });
      setAutoConfigResult(r.data);
      if (r.data?.success) {
        toast.success('Webhook הוגדר אוטומטית! שלח לעצמך הודעה לבדיקה');
      } else {
        toast.error('נכשל: ' + (r.data?.error || 'ראה פרטים למטה'));
      }
    } catch (e) {
      setAutoConfigResult({ success: false, error: e?.response?.data?.detail || e.message });
      toast.error('שגיאה בהגדרת webhook');
    }
    setConfiguringWh(false);
  };

  useEffect(() => { loadLogs(); }, []);

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('הועתק!');
  };

  const waConnected = !!(wa.api_key && wa.api_key.length > 10);

  return (
    <div className="space-y-4 pb-6" data-testid="admin-assistant-section">
      {/* Header */}
      <div className="bg-gradient-to-l from-emerald-700 via-emerald-800 to-emerald-900 text-white rounded-2xl p-5 shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-white/15 p-2.5 rounded-xl"><Bot className="w-7 h-7" /></div>
          <div>
            <h1 className="text-2xl font-bold">עוזר אישי AI בוואטסאפ</h1>
            <p className="text-emerald-100 text-sm">Claude Sonnet 4.5 • שלח הודעה לעצמך וקבל תגובה מיידית</p>
          </div>
        </div>
      </div>

      {/* Status banner */}
      <div className={`rounded-2xl p-4 shadow border flex items-center gap-3 ${waConnected ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`}>
        {waConnected ? <CheckCircle2 className="w-7 h-7 text-emerald-600 shrink-0" /> : <AlertCircle className="w-7 h-7 text-amber-600 shrink-0" />}
        <div className="flex-1">
          <div className={`font-bold text-lg ${waConnected ? 'text-emerald-800' : 'text-amber-800'}`}>
            {waConnected ? '✅ WhatsApp מחובר' : '⚠️ עוד לא חיברת את ה-WhatsApp שלך'}
          </div>
          <div className={`text-sm ${waConnected ? 'text-emerald-700' : 'text-amber-700'}`}>
            {waConnected
              ? `שלח לעצמך (${config?.owner_phone}) הודעה בוואטסאפ ותראה את הבוט עונה.`
              : 'מלא את 2 השלבים למטה - זה לוקח דקה.'}
          </div>
        </div>
        {waConnected && (
          <button
            onClick={testConnection}
            disabled={testingConnection}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1 disabled:opacity-50"
            data-testid="test-connection-btn"
          >
            {testingConnection ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            שלח בדיקה
          </button>
        )}
      </div>

      {/* STEP 1 - Get API key */}
      <div className="bg-white rounded-2xl p-5 shadow border-2 border-emerald-200">
        <div className="flex items-center gap-2 mb-3">
          <div className="bg-emerald-100 text-emerald-800 rounded-full w-8 h-8 flex items-center justify-center font-bold">1</div>
          <div className="font-bold text-lg text-stone-800">חיבור הוואטסאפ שלך ב-WaSenderAPI</div>
        </div>
        <div className="text-sm text-stone-600 mb-3 pr-10 space-y-1">
          <div>• לחץ על הכפתור למטה - ייפתח אתר WaSenderAPI.</div>
          <div>• הירשם/התחבר (חינמי 7 ימים).</div>
          <div>• צור "Session" חדש → <b>סרוק את ה-QR עם WhatsApp במכשיר</b> (הגדרות ← מכשירים מקושרים).</div>
          <div>• בלוח הבקרה תראה את ה-<b>API Key</b> (לחץ על אייקון 🔑) - העתק אותו.</div>
        </div>
        <div className="pr-10">
          <a
            href="https://www.wasenderapi.com/dashboard"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-bold shadow"
            data-testid="open-wasender-btn"
          >
            <QrCode className="w-4 h-4" />
            פתח WaSenderAPI וסרוק QR
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* STEP 2 - Paste API Key */}
      <div className="bg-white rounded-2xl p-5 shadow border-2 border-emerald-200">
        <div className="flex items-center gap-2 mb-3">
          <div className="bg-emerald-100 text-emerald-800 rounded-full w-8 h-8 flex items-center justify-center font-bold">2</div>
          <div className="font-bold text-lg text-stone-800">הדבק את ה-API Key</div>
        </div>
        <div className="text-sm text-stone-600 mb-3 pr-10">הדבק כאן את ה-API Key שהעתקת מ-WaSenderAPI (שלב 1).</div>
        <div className="pr-10 space-y-3">
          <div className="relative">
            <KeyRound className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type={showKey ? 'text' : 'password'}
              value={wa.api_key}
              onChange={e => setWa({ ...wa, api_key: e.target.value })}
              placeholder="הדבק API Key כאן..."
              className="w-full border-2 border-stone-300 rounded-xl px-10 py-3 focus:outline-none focus:border-emerald-500 text-right"
              dir="ltr"
              data-testid="wa-api-key-input"
            />
            <button
              type="button"
              onClick={() => setShowKey(s => !s)}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
              data-testid="toggle-key-visibility"
            >
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <button
            onClick={saveWaConfig}
            disabled={savingWa || !wa.api_key.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2"
            data-testid="save-wa-btn"
          >
            {savingWa ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            שמור וחבר
          </button>
        </div>
      </div>

      {/* STEP 3 - Webhook URL */}
      <div className="bg-white rounded-2xl p-5 shadow border-2 border-emerald-200">
        <div className="flex items-center gap-2 mb-3">
          <div className="bg-emerald-100 text-emerald-800 rounded-full w-8 h-8 flex items-center justify-center font-bold">3</div>
          <div className="font-bold text-lg text-stone-800">הגדרת Webhook - 2 אפשרויות</div>
        </div>

        {/* Option A - Automatic */}
        <div className="pr-10 mb-4 bg-emerald-50 border-2 border-emerald-400 rounded-xl p-4">
          <div className="font-bold text-emerald-900 mb-2">⚡ אפשרות A - הגדרה אוטומטית (מומלץ!)</div>
          <div className="text-xs text-emerald-800 mb-2 space-y-1">
            <div>1. ב-WaSenderAPI ← לחץ על הפרופיל שלך (פינה ימנית עליונה) ← <b>Profile Settings</b>.</div>
            <div>2. גלול ל-<b>"Personal Access Tokens"</b> ← Create Token ← העתק אותו.</div>
            <div>3. הדבק כאן ולחץ "הגדר אוטומטית" - אני אגדיר את ה-Webhook באופן אוטומטי.</div>
          </div>
          <div className="flex gap-2 mb-2">
            <input
              type={showPat ? 'text' : 'password'}
              value={pat}
              onChange={e => setPat(e.target.value)}
              placeholder="הדבק Personal Access Token..."
              className="flex-1 border-2 border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
              dir="ltr"
              data-testid="pat-input"
            />
            <button
              type="button"
              onClick={() => setShowPat(s => !s)}
              className="text-stone-600 hover:text-stone-900 p-2 rounded-lg bg-stone-100"
              data-testid="toggle-pat-visibility"
            >
              {showPat ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button
              onClick={autoConfigureWebhook}
              disabled={!pat.trim() || configuringWh}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1"
              data-testid="auto-configure-webhook-btn"
            >
              {configuringWh ? <Loader2 className="w-4 h-4 animate-spin" /> : '⚡'}
              הגדר אוטומטית
            </button>
          </div>
          {autoConfigResult && (
            <div className={`text-xs p-2 rounded ${autoConfigResult.success ? 'bg-emerald-100 text-emerald-900' : 'bg-red-100 text-red-900'}`}>
              {autoConfigResult.success ? '✅ ' : '❌ '}
              {autoConfigResult.success
                ? `הוגדר! ${autoConfigResult.sessions?.filter(s => s.ok).length || 0} Sessions עודכנו. שלח לעצמך הודעה עכשיו לבדיקה 📱`
                : `נכשל: ${autoConfigResult.error || JSON.stringify(autoConfigResult).slice(0, 200)}`}
            </div>
          )}
        </div>

        {/* Option B - Manual */}
        <div className="pr-10 bg-stone-50 border border-stone-300 rounded-xl p-4">
          <div className="font-bold text-stone-800 mb-2">🔧 אפשרות B - הגדרה ידנית</div>
          <div className="text-xs text-stone-700 mb-2 space-y-1">
            <div>1. ב-WaSenderAPI ← Sessions ← לחץ על ה-Session ← <b>Edit</b>.</div>
            <div>2. בתוך העריכה, יש שדה <b>Webhook URL</b> - הדבק את הכתובת מתחת.</div>
            <div>3. סמן <b>Webhook Enabled</b> וב-Events סמן <b>messages.upsert</b>.</div>
            <div>4. Save.</div>
          </div>
          <div className="flex gap-2 items-center bg-white border-2 border-emerald-300 rounded-lg p-2">
            <code className="flex-1 text-xs break-all text-emerald-900 font-bold" data-testid="assistant-webhook-url">{webhookUrl}</code>
            <button
              onClick={copyWebhook}
              className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-3 py-1.5 rounded-lg flex items-center gap-1 font-bold"
              data-testid="copy-webhook-btn"
            >
              {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'הועתק' : 'העתק'}
            </button>
          </div>
        </div>
      </div>

      {/* Done */}
      {waConnected && (
        <div className="bg-emerald-100 rounded-2xl p-5 border-2 border-emerald-400">
          <div className="font-bold text-emerald-900 mb-2 text-lg">🎉 מוכן! איך להשתמש:</div>
          <div className="text-sm text-emerald-900 space-y-1">
            <div>• שלח הודעת WhatsApp לעצמך: <b dir="ltr">+972{config?.owner_phone?.replace(/^0/, '')}</b></div>
            <div>• דוגמאות: <i>"תראה הזמנות היום"</i> · <i>"מלאי יניב"</i> · <i>"סיכום"</i> · <i>"פייסבוק"</i></div>
            <div>• הבוט עונה תוך 5-30 שניות ישירות בוואטסאפ 📱</div>
          </div>
        </div>
      )}

      {/* Config badges */}
      {config && (
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-1 rounded-md flex items-center gap-1">
            <Phone className="w-3 h-3" /> בעלים: {config.owner_phone}
          </span>
          <span className="bg-sky-50 text-sky-800 border border-sky-200 px-2 py-1 rounded-md">מודל: {config.model}</span>
          <span className="bg-amber-50 text-amber-800 border border-amber-200 px-2 py-1 rounded-md">אבטחה: רק המספר שלך מקבל מענה</span>
        </div>
      )}

      {/* Webhook activity monitor */}
      <div className="bg-white rounded-2xl p-4 shadow border border-stone-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 font-bold text-stone-700">
            <Activity className="w-4 h-4 text-emerald-600" /> ניטור חי - Webhook (הודעות שנקלטות מוואטסאפ)
          </div>
          <button
            onClick={loadLogs}
            disabled={loadingLogs}
            className="text-xs bg-stone-100 hover:bg-stone-200 px-3 py-1.5 rounded-lg flex items-center gap-1"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingLogs ? 'animate-spin' : ''}`} /> רענן
          </button>
        </div>
        {webhookLogs.length === 0 ? (
          <div className="text-sm text-stone-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
            ⚠️ לא התקבלו הודעות webhook עדיין. זה אומר שהאירוע <b>messages.upsert</b> לא מוגדר ב-WaSenderAPI או שה-URL שגוי. ודא שחזרת ל-WaSenderAPI → Session שלך → Settings → Webhooks → הדבק את ה-URL וסמן את <b>messages.upsert</b> (או "Enable all events").
          </div>
        ) : (
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            {webhookLogs.map((l, i) => {
              const ownerMatch = l.extracted_phone && l.extracted_phone.replace(/^972/, '').replace(/^0/, '') === (config?.owner_phone || '').replace(/^0/, '');
              return (
                <div key={i} className={`text-xs border rounded-lg p-2 ${ownerMatch ? 'bg-emerald-50 border-emerald-200' : 'bg-stone-50 border-stone-200'}`}>
                  <div className="flex items-center gap-2 text-[10px] text-stone-500 mb-1">
                    <span>{new Date(l.at).toLocaleTimeString('he-IL')}</span>
                    <span className="bg-stone-200 px-1.5 rounded">{l.event || '?'}</span>
                    {l.from_me ? <span className="bg-blue-100 text-blue-700 px-1.5 rounded">fromMe</span> : <span className="bg-green-100 text-green-700 px-1.5 rounded">incoming</span>}
                    {ownerMatch ? <span className="bg-emerald-600 text-white px-1.5 rounded">✓ בעלים</span> : <span className="bg-red-100 text-red-700 px-1.5 rounded">❌ לא זוהה</span>}
                  </div>
                  <div className="text-stone-800" dir="rtl"><b>טלפון:</b> <span dir="ltr">{l.extracted_phone || '(ריק)'}</span> | <b>טקסט:</b> {l.extracted_text || '(ריק)'}</div>
                </div>
              );
            })}
          </div>
        )}
        <div className="text-[11px] text-stone-500 mt-2">
          💡 כדי לראות הודעה חדשה: שלח WhatsApp לעצמך ואז לחץ "רענן". אם מופיע "✓ בעלים" ירוק - הבוט אמור לענות.
        </div>
      </div>

      {/* Chat test area */}
      <div className="bg-white rounded-2xl p-4 shadow border border-stone-200">
        <div className="text-sm text-stone-600 mb-2 font-semibold">🚀 בדיקה מהירה כאן (בלי WhatsApp):</div>
        <div className="flex flex-wrap gap-2 mb-3">
          {QUICK_PROMPTS.map(p => (
            <button
              key={p.label}
              onClick={() => send(p.text)}
              disabled={sending}
              className="bg-stone-100 hover:bg-emerald-50 hover:border-emerald-400 border border-stone-300 text-stone-800 text-sm px-3 py-1.5 rounded-full transition disabled:opacity-50"
              data-testid={`quick-prompt-${p.label.replace(/\s/g, '-')}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="border border-stone-200 rounded-xl overflow-hidden flex flex-col" style={{ height: '420px' }}>
          <div className="flex items-center justify-between p-2 bg-stone-50 border-b border-stone-200">
            <div className="flex items-center gap-2 text-sm font-bold text-stone-700"><MessageSquare className="w-4 h-4" /> צ'אט בדיקה</div>
            <button
              onClick={clearHistory}
              className="text-red-600 hover:bg-red-50 p-1.5 rounded-lg flex items-center gap-1 text-xs"
              data-testid="clear-history-btn"
            >
              <Trash2 className="w-3.5 h-3.5" /> נקה
            </button>
          </div>

          <div
            ref={chatScrollRef}
            className="flex-1 overflow-y-auto p-3 space-y-2 bg-stone-50"
            data-testid="assistant-messages"
            style={{ overscrollBehavior: 'contain' }}
          >
            {messages.length === 0 && (
              <div className="text-center text-stone-400 mt-10">
                <Bot className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <div className="text-sm">שלח הודעה או בחר פקודה מהירה</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] rounded-2xl px-3.5 py-2 whitespace-pre-wrap text-sm shadow-sm ${
                    m.role === 'user' ? 'bg-emerald-600 text-white rounded-bl-md' : 'bg-white border border-stone-200 text-stone-800 rounded-br-md'
                  }`}
                  data-testid={`msg-${m.role}-${i}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-stone-200 rounded-2xl px-3.5 py-2 flex items-center gap-2 text-stone-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> חושב...
                </div>
              </div>
            )}
          </div>

          <div className="p-2 border-t border-stone-200 flex gap-2 bg-white">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="שאל בעברית..."
              disabled={sending}
              className="flex-1 border border-stone-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-right text-sm"
              data-testid="assistant-input"
            />
            <button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white px-4 rounded-lg flex items-center gap-1 font-bold text-sm"
              data-testid="assistant-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              שלח
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
